import { isDefined } from "@clipboard-health/util-ts";
import {
  type CancelShiftPolicy,
  type ClipboardScoreProfileResponse,
  useGetClipboardScoreProfile,
} from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { formatDurationRangeLabel } from "@src/appV2/redesign/utils/formatDurationLabel";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { differenceInHours, parseISO } from "date-fns";
import { useMemo } from "react";

import { useGetShiftDetailsData } from "../../api/useGetShiftDetailsData";

export interface UseCancelShiftPageDataParams {
  shiftId: string;
  currentDate: Date;
}

export interface CancelShiftPageData {
  isLoading: boolean;
  isError: boolean;
  currentScore: number;
  newScore: number;
  scoreChange: number;
  cancellationPolicy: CancelShiftPolicy | undefined;
  leadTimeHours: number;
  hasCancellationCredit: boolean;
  emergencyCreditStats: ClipboardScoreProfileResponse["emergencyCreditStats"] | undefined;
  policyLabel: string;
  shiftStartDate: Date | undefined;
}

function findCancellationPolicy(
  policies: CancelShiftPolicy[] | undefined,
  leadTimeHours: number
): CancelShiftPolicy | undefined {
  if (!isDefined(policies)) {
    return undefined;
  }

  return policies.find((policy) => {
    const [minHours, maxHours] = policy.leadTime;
    const meetsMinimum = !isDefined(minHours) || leadTimeHours >= minHours;
    const meetsMaximum = !isDefined(maxHours) || leadTimeHours < maxHours;
    return meetsMinimum && meetsMaximum;
  });
}

export function useCancelShiftPageData(params: UseCancelShiftPageDataParams): CancelShiftPageData {
  const { shiftId, currentDate } = params;

  const { userId } = useDefinedWorker();

  const { shift, isLoading: isShiftLoading, shiftError } = useGetShiftDetailsData({ shiftId });

  const {
    data: clipboardScoreProfile,
    isLoading: isClipboardScoreLoading,
    isError: isClipboardScoreError,
  } = useGetClipboardScoreProfile(
    userId,
    { enabled: isDefined(userId) },
    shift?.agentReq,
    shift?.facilityId
  );

  const isLoading = isShiftLoading || isClipboardScoreLoading;
  const isError = isDefined(shiftError) || isClipboardScoreError;

  const shiftStartDate = useMemo(() => {
    if (!isDefined(shift?.start)) {
      return undefined;
    }
    return parseISO(shift.start);
  }, [shift?.start]);

  const leadTimeHours = useMemo(() => {
    if (!isDefined(shiftStartDate)) {
      return 0;
    }
    return differenceInHours(shiftStartDate, currentDate);
  }, [shiftStartDate, currentDate]);

  const cancellationPolicy = useMemo(() => {
    return findCancellationPolicy(clipboardScoreProfile?.policy?.cancelShift, leadTimeHours);
  }, [clipboardScoreProfile?.policy?.cancelShift, leadTimeHours]);

  const emergencyCreditStats = clipboardScoreProfile?.emergencyCreditStats;

  const hasCancellationCredit = useMemo(() => {
    return (emergencyCreditStats?.cancellation.totalAvailableCredits ?? 0) > 0;
  }, [emergencyCreditStats?.cancellation.totalAvailableCredits]);

  const scoreChange = useMemo(() => {
    if (!isDefined(cancellationPolicy)) {
      return 0;
    }
    return cancellationPolicy.points;
  }, [cancellationPolicy]);

  const currentScore = clipboardScoreProfile?.clipboardScore ?? 0;

  const newScore = useMemo(() => {
    return currentScore + scoreChange;
  }, [currentScore, scoreChange]);

  const policyLabel = useMemo(() => {
    const [from, to] = cancellationPolicy?.leadTime ?? [0, undefined];
    return `${formatDurationRangeLabel(from ?? 0, to ?? undefined, "hour", {
      format: "abbreviated",
      keepOriginalUnit: true,
    })} cancellation`;
  }, [cancellationPolicy?.leadTime]);

  return {
    isLoading,
    isError,
    currentScore,
    newScore,
    scoreChange,
    cancellationPolicy,
    leadTimeHours,
    hasCancellationCredit,
    emergencyCreditStats,
    policyLabel,
    shiftStartDate,
  };
}
